import { useEffect, useRef, useState } from 'react';
import { Autocomplete, Button, Stack, TextField, type AutocompleteOption } from '@citibox-panel/ui';
import type { LocationMatch, UserSearchHit, UserUpdate } from '@citibox-panel/api-client';
import { isAnonymized } from '../../../lib/isAnonymized';
import { isValidEmailFormat } from '../lib/emailFormat';
import { isStandIn, standInOption } from '../lib/addressOption';
import { useUpdateUser } from '../hooks/useUpdateUser';
import { useAddressSuggestions } from '../hooks/useAddressSuggestions';
import { useReportFailure } from '../../search-shell/hooks/useErrorNotice';
import { UserLayout } from './UserLayout';
import { DeleteAccountSection } from './DeleteAccountSection';
import { BlockAccountSection } from './BlockAccountSection';
import { UnblockAccountSection } from './UnblockAccountSection';
import { canUnblock } from '../lib/unblockEligibility';

// TODO: i18n — copy en ES hasta Fase 1.
const EMAIL_ERROR = 'Email con formato no válido';
const ADDRESS_READ_ERROR = 'No se pueden cargar direcciones ahora';
const WRITE_ERROR = 'No se han podido guardar los datos';
const SAVED = 'Datos actualizados';
const NOTICE_ID = 'update-account';

export interface EditAccountSectionProps {
  account: UserSearchHit | undefined;
  isPending: boolean;
  phoneKey: string;
  updateUser: (id: string, changes: UserUpdate) => Promise<void>;
  deleteUser: (id: string, amazonExclusion: boolean, reason?: string) => Promise<void>;
  blockUser: (id: string, reason?: string) => Promise<void>;
  unblockUser: (id: string, reason: string) => Promise<void>;
  searchLocations: (q: string) => Promise<LocationMatch[]>;
  // The tab's label is the titular's name and the HOST owns tabs, so the rename is reported up
  // instead of this slice reaching for `useSearchTabs` — which would also make it untestable
  // without a router.
  onRenamed?: (name: string) => void;
  // The screen's live region belongs to `apps/shell` (CBX-11386). This slice words the sentence
  // and knows nothing about where it lands.
  onAnnounce?: (message: string) => void;
}

interface Draft {
  name: string;
  email: string;
  // The chosen building, or the stand-in for the address the ficha loaded with. `null` only for an
  // account that has no address at all (carrier pre-registration).
  address: AutocompleteOption | null;
  // What is typed in the combobox, which is NOT the selection: MUI puts the selected label back on
  // blur, and the term is what the suggestions read answers.
  addressInput: string;
  // Whether the agent has typed in the address field at all. The field OPENS holding the loaded
  // address, and that text must never be used as a search term: it would fire a read for a string
  // the suggestions cannot match, and — because the façade blocks a `loading` combobox — leave the
  // field disabled for the whole of that pointless round trip, exactly when the agent reaches for
  // it. A flag and not a comparison against the current address, so clearing the field and typing
  // the same text back still searches.
  addressTouched: boolean;
}

const draftFrom = (account: UserSearchHit): Draft => ({
  name: account.name,
  email: account.email,
  address: standInOption(account.address),
  addressInput: account.address,
  addressTouched: false,
});

// The account section of the «Usuario» ficha, and the owner of its edit mode: the mode itself, the
// draft, the dirty check and the focus. It wraps `UserLayout` rather than living inside it, because
// the mode decides what goes in BOTH of that component's slots — the three field controls and the
// button stack — and a component rendered inside a slot cannot decide the other one.
export function EditAccountSection({
  account,
  isPending,
  phoneKey,
  updateUser,
  deleteUser,
  blockUser,
  unblockUser,
  searchLocations,
  onRenamed,
  onAnnounce,
}: EditAccountSectionProps) {
  const [draft, setDraft] = useState<Draft | null>(null);
  const nameRef = useRef<HTMLInputElement>(null);
  const editRef = useRef<HTMLButtonElement>(null);
  // Whether this card has ever been in edit mode. Without it the focus effect below would grab
  // «Editar» on first mount and steal the focus from whatever the shell had just placed.
  const hasEdited = useRef(false);
  // Whether a block was confirmed and its repaint is still on the way. A flag and not a direct
  // `focus()` call from the callback: at that moment the modal is still on screen, so MUI's focus
  // trap pulls the focus straight back in and then hands it to the padlock as the dialog exits —
  // the very control the incoming account is about to withdraw. The effect below waits for that
  // repaint, which is the first moment there IS a settled row to focus.
  const justBlocked = useRef(false);
  // The same, for the open padlock.
  const justUnblocked = useRef(false);

  // Called unconditionally and ahead of every gate, for the reason `DeleteAccountSection` spells
  // out: the Rules of Hooks forbid skipping a call on some renders but not others, and this same
  // instance can re-render with no account (a pending refetch) or with a status that just flipped.
  const mutation = useUpdateUser(account?.id ?? '', phoneKey, updateUser, onRenamed);
  // Empty — and so below the three-character floor — until the agent types: a card in read mode
  // reads nothing, and neither does a form that just opened holding the loaded address.
  const suggestions = useAddressSuggestions(
    draft?.addressTouched === true ? draft.addressInput : '',
    searchLocations,
  );

  // ADR 0021: a failed WRITE goes to the floating notice, never to a banner inside the form.
  // `reset()` is what puts «Guardar» back to idle once the failure has been reported, while the
  // notice stays up until the agent dismisses it. The error itself goes along so a permission
  // refusal — which the host answers with its own access screen — raises no notice of its own.
  useReportFailure(
    NOTICE_ID,
    mutation.isError ? WRITE_ERROR : null,
    () => {
      mutation.reset();
    },
    mutation.error,
  );

  const editing = draft !== null;

  useEffect(() => {
    if (editing) {
      hasEdited.current = true;
      nameRef.current?.focus();
      return;
    }
    // The button the agent pressed was UNMOUNTED by the mode change — the task in-line editing
    // adds that a modal never had. Without this the focus falls to <body>.
    if (hasEdited.current) editRef.current?.focus();
  }, [editing]);

  // The other way this card orphans the focus: the padlock is pressed, the account comes back
  // blocked, and the gate inside the action withdraws the button that was pressed. Same remedy as
  // the mode change above, and the same survivor — «Editar datos» stays, because a blocked account
  // is still editable.
  useEffect(() => {
    if (!justBlocked.current || account?.active !== false) return;
    justBlocked.current = false;
    editRef.current?.focus();
  }, [account?.active]);

  // Its mirror for the unblock, keyed on the unblock being WITHDRAWN rather than on `active`
  // turning true: an invited courier comes back `activo` but still `active: false`, with no padlock
  // in its place, and an effect waiting on `active` would never fire — dropping the focus to
  // <body> and leaving the flag armed for some later change. «Editar datos» and never the block
  // padlock that may appear: one Enter too many must not undo what was just done.
  const unblockOffered = account !== undefined && canUnblock(account);
  useEffect(() => {
    if (!justUnblocked.current || unblockOffered) return;
    justUnblocked.current = false;
    editRef.current?.focus();
  }, [unblockOffered]);

  if (isPending || account === undefined) {
    return <UserLayout account={account} isPending={isPending} />;
  }

  const anonymized = isAnonymized(account.status);

  const bajaSection = anonymized ? undefined : (
    <DeleteAccountSection account={account} phoneKey={phoneKey} deleteUser={deleteUser} />
  );

  const readMode = (
    <UserLayout
      account={account}
      isPending={false}
      // The design puts the two writes of this card in two different places (capturas de PM,
      // 2026-09-11): the quiet one on the status row, the destructive one at the foot. Ascending
      // consequence still holds — it spans the card instead of a stack — and the separation buys
      // «Editar datos» the thing a stacked pair could not give it: it sits next to the name it
      // edits. `variant="text" sentenceCase` and not `link`: the `link` look carries an underline
      // at rest, which the captures do not draw, and this is the idiom the other quiet action in
      // the product already uses («Editar» beside the location tab's filter chips). `bold` because
      // the captures draw it bold and MUI's button weight is 500 — a façade prop (CBX-11387), never
      // an sx override from here.
      // Attached to the chip, because that is what it turns red. Passed ONLY by this read-mode
      // layout: the edit-mode one below never mounts it, so the action is gone while correcting —
      // the same way «Editar datos» is, and for the same reason. That absence is a property of
      // the mode's layout, NOT a safety exclusion: do not "fix" it into the mutual exclusion this
      // slice already rejected for the baja.
      statusAction={
        anonymized ? undefined : (
          // Each padlock drops out by its own rule, and the two rules exclude each other, so the
          // row never shows both.
          <>
            <BlockAccountSection
              account={account}
              phoneKey={phoneKey}
              blockUser={blockUser}
              // The padlock is withdrawn by its own gate as the blocked account lands, so the focus
              // is orphaned exactly as it is when the edit form closes — and it goes to the same
              // place, for the same reason: «Editar datos» is what is left of this row.
              onBlocked={() => {
                justBlocked.current = true;
              }}
              {...(onAnnounce ? { onAnnounce } : {})}
            />
            <UnblockAccountSection
              account={account}
              phoneKey={phoneKey}
              unblockUser={unblockUser}
              onUnblocked={() => {
                justUnblocked.current = true;
              }}
              {...(onAnnounce ? { onAnnounce } : {})}
            />
          </>
        )
      }
      headerAction={
        anonymized ? undefined : (
          <Button
            ref={editRef}
            variant="text"
            sentenceCase
            bold
            onClick={() => {
              setDraft(draftFrom(account));
            }}
          >
            Editar datos
          </Button>
        )
      }
      action={bajaSection}
    />
  );

  // The anonymized gate comes BEFORE the draft, and that order is load-bearing since the design
  // stopped hiding «Dar de baja» during edit (capture 2026-09-11): the agent can now complete a
  // baja with a half-filled form open, and the account comes back anonymized. Asking about the
  // draft first would keep the form over a record that no longer has anything to correct.
  if (draft === null || anonymized) return readMode;

  const name = draft.name.trim();
  const email = draft.email.trim();
  const chosen = draft.address !== null && !isStandIn(draft.address) ? draft.address : null;

  const emailMalformed = email !== '' && !isValidEmailFormat(email);
  // Required ON SCREEN, all three, even though none is required in the body. A carrier
  // pre-registration opens with two of them empty and cannot be saved until the agent fills all
  // three — the SPEC's known limitation, and the population CC still resolves in the legacy.
  const complete = name !== '' && email !== '' && draft.address !== null;
  const dirty = name !== account.name || email !== account.email || chosen !== null;

  const changes: UserUpdate = {
    ...(name !== account.name ? { name } : {}),
    ...(email !== account.email ? { email } : {}),
    // Guarded on the type and not merely on the sentinel: a real location id is an integer, and
    // anything else reaching the body would be a second definition of what a location is.
    ...(chosen !== null && typeof chosen.id === 'number' ? { locationId: chosen.id } : {}),
  };

  // What the dropdown says when it has no rows to show — three different facts, and calling all
  // three «no results» would be a lie in two of them.
  // TODO: i18n
  const addressEmptyLine = suggestions.isSearching
    ? 'Buscando…'
    : suggestions.isTooShort
      ? 'Escribe al menos 3 caracteres'
      : `Sin resultados para "${draft.addressInput}"`;

  const leaveEdit = (): void => {
    setDraft(null);
  };

  return (
    <UserLayout
      account={account}
      isPending={false}
      edit={{
        name: (
          <TextField
            ref={nameRef}
            aria-label="NOMBRE"
            required
            fullWidth
            // The read column is set in body2 and these controls replace it in place, so at MUI's
            // default `body1` the form would re-type the whole column one step bigger than the
            // values it stands in for. All three carry it, or the asymmetry just moves.
            textVariant="body2"
            value={draft.name}
            disabled={mutation.isPending}
            onChange={(value) => {
              setDraft({ ...draft, name: value });
            }}
          />
        ),
        email: (
          <TextField
            aria-label="EMAIL"
            required
            fullWidth
            textVariant="body2"
            value={draft.email}
            disabled={mutation.isPending}
            error={emailMalformed}
            {...(emailMalformed ? { helperText: EMAIL_ERROR } : {})}
            onChange={(value) => {
              setDraft({ ...draft, email: value });
            }}
          />
        ),
        address: (
          <Autocomplete
            aria-label="DIRECCIÓN"
            required
            fullWidth
            textVariant="body2"
            // EVERY row the read returned, uncapped: the dropdown scrolls (decided 2026-09-11).
            // The stand-in is prepended so the loaded address stays selectable until a real
            // building replaces it — it is not one of the suggestions and never comes back in one.
            options={
              draft.address !== null && isStandIn(draft.address)
                ? [draft.address, ...suggestions.options]
                : suggestions.options
            }
            value={draft.address}
            inputValue={draft.addressInput}
            // FAILS OPEN: a suggestions read that died disables THIS field and nothing else, so
            // the agent can still correct a name or an email while addresses are unavailable.
            disabled={mutation.isPending || suggestions.isError}
            {...(suggestions.isError ? { helperText: ADDRESS_READ_ERROR } : {})}
            // Deliberately NOT the façade's `loading`, which is the one prop of that API this
            // field cannot use: `loading` blocks the control (`blocked = disabled || loading`),
            // which is right for a `Select` reading its options once and fatal for a
            // search-as-you-type — the field would disable itself on the third keystroke, mid-word,
            // every single time. The distinction `loading` exists to draw (an empty list that is
            // still being read vs. one that came back empty) is carried by the dropdown's own line
            // instead, which is where the agent is already looking.
            noOptionsText={addressEmptyLine}
            onChange={(option) => {
              setDraft({ ...draft, address: option });
            }}
            onInputChange={(text) => {
              setDraft({ ...draft, addressInput: text, addressTouched: true });
            }}
          />
        ),
      }}
      // The commit actions take the slot «Editar datos» vacated — the status row, not the foot
      // (capture 2026-09-11). They replace the trigger because they ARE the same decision in its
      // next state, so the eye does not have to hunt for where the mode went.
      //
      // `size="small"` on BOTH, by product decision (2026-09-11): at `medium` the three controls
      // measure the same box — MUI trades a pixel of padding for the outlined border precisely so
      // they do — but a filled pill reads heavier than bare text, and the ask was to make these
      // two sit lighter on the row. The knowing cost: `small` also drops the label to 13px, so
      // they no longer share «Editar datos»'s 14px typography. Both of them, never one, or the
      // affirmative and the escape would disagree on size. `bold` on both for the same reason it
      // is on «Editar datos» — which also recovers half of what `small` cost: the weight matches
      // across the three actions of the card even though the size no longer does.
      headerAction={
        // `mb` on the button row and NOT on `UserLayout`'s slot: the slot is shared with read
        // mode's «Editar datos», which the design draws tight against the chip. The two pills are
        // taller than that text action, so without this they crowd the NOMBRE control right under
        // them. One step of the 8px grid.
        <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
          <Button
            size="small"
            sentenceCase
            bold
            loading={mutation.isPending}
            disabled={!dirty || !complete || emailMalformed}
            onClick={() => {
              mutation.mutate(changes, {
                // Per-call, not the hook's own onSuccess: this runs strictly AFTER TanStack has
                // awaited that one — which chains the account refetch — so by the time it fires
                // the ficha behind the form already holds the corrected values. Leaving edit mode
                // any earlier would flash the pre-write account.
                onSuccess: () => {
                  onAnnounce?.(SAVED);
                  leaveEdit();
                },
              });
            }}
          >
            Guardar
          </Button>
          <Button
            variant="outlined"
            size="small"
            sentenceCase
            bold
            // Abandoning a write already on the wire would leave the agent believing nothing was
            // sent, with no way to find out.
            disabled={mutation.isPending}
            onClick={leaveEdit}
          >
            Cancelar
          </Button>
        </Stack>
      }
      // «Dar de baja» STAYS, which reverses the mutual exclusion the SPEC planned (capture
      // 2026-09-11). What keeps it safe is no longer exclusion but distance and friction: the
      // commit actions are on the status row, this is at the foot, and the baja carries its own
      // double friction. The race it opens is handled by the anonymized gate above.
      action={bajaSection}
    />
  );
}
